import React, { useEffect, useState } from "react";

import { SettingsCard } from "./SettingsCard";
import { SearchBar } from "../../components/tailwind";

enum Section {
  DataPipeline = "data pipeline",
  AIGeneration = "ai generation",
  Translation = "translation",
  TextControl = "text control",
  Logs = "logs",
  Other = "other",
  Account = "account",
}

type URLParts = {
  scope: "manage" | "customer";
  path: string;
};

type SettingsSummary = {
  section: Section;
  URLParts: URLParts;
  header: string;
  description: string;
  example?: React.ReactNode;
  visibleWhen?: "showBilling" | "isAuthenticated";
};

type SettingsSectionGroup = {
  section: Section;
  title: string;
  icon: string;
  summaries: SettingsSummary[];
};

const SETTINGS_SECTIONS: SettingsSectionGroup[] = [
  {
    section: Section.DataPipeline,
    title: "Data Pipeline",
    icon: "data_object",
    summaries: [
      {
        section: Section.DataPipeline,
        URLParts: { scope: "manage", path: "planner/document-structures" },
        header: "Document Structures & Sections",
        description:
          "Manage how your Documents should be structured with sections.",
        example: (
          <>
            Every <i>Document</i> is made up of <i>Sections</i> which has its
            own settings for formatting and what content it should hold.
          </>
        ),
      },
      {
        section: Section.DataPipeline,
        URLParts: { scope: "manage", path: "pipelines" },
        header: "Agents",
        description: "Chain together actions to automate workflows.",
        example: (
          <>
            An <i>Agent</i> is a sequence of actions which are run on a{" "}
            <i>Document</i> which can be triggered by events or manually.
          </>
        ),
      },
      {
        section: Section.DataPipeline,
        URLParts: { scope: "customer", path: "actions/" },
        header: "Actions",
        description: "Automate actions which should be triggered on an event.",
        example: (
          <>
            An <i>Event</i> can run a single task or an <i>Agent</i> on a{" "}
            <i>Document</i> when it occurs.
          </>
        ),
      },
      {
        section: Section.DataPipeline,
        URLParts: { scope: "manage", path: "customer-fields" },
        header: "PIM Fields",
        description:
          "Add fields in Textual representing fields in your PIM and add processing rules to their data.",
      },
      {
        section: Section.DataPipeline,
        URLParts: { scope: "customer", path: "mappings/" },
        header: "Key-Value Mappings",
        description: "Map Textual tags to key-value pairs in your data.",
        example: (
          <>
            A <i>Key-Value Mapping</i> connects Textual tags to key-value pairs
            in your data. To map key-values, the key must first be{" "}
            <i>Key Mapped</i> to <i>Map to a vocabulary tag</i>.
          </>
        ),
      },
    ],
  },
  {
    section: Section.AIGeneration,
    title: "AI Generation",
    icon: "smart_toy",
    summaries: [
      {
        section: Section.AIGeneration,
        URLParts: { scope: "manage", path: "gpt/finetuning/" },
        header: "Fine-tuning",
        description: "Train your own fine-tuned AI model.",
        example: (
          <>
            Provide quality assured data to fine-tune a model on a specific use
            case. This will cause the model to generate texts which are more
            similar to the data it&apos;s trained on and can give better and
            more accurate results.
          </>
        ),
      },
      {
        section: Section.AIGeneration,
        URLParts: { scope: "manage", path: "gpt/prompts/" },
        header: "Prompt Designer",
        description: "Manage prompts and prompt groups.",
        example: (
          <>
            A <i>Prompt</i> is a collection of instructions and settings which
            are used to generate text with AI.
            <br />A <i>Prompt Group</i> is a collection of prompts which should
            be run at the same time.
          </>
        ),
      },
      {
        section: Section.AIGeneration,
        URLParts: { scope: "manage", path: "fieldsets" },
        header: "Input Schemas",
        description:
          "Define groups of data which can be used as input for AI generation.",
        example: (
          <>
            An <i>Input Schema</i> is a collection of data which is used as
            input for AI generation together with an instruction.
            <br />
            Which <i>Input Schema</i> to use is selected when configuring a{" "}
            <i>Prompt</i>.
          </>
        ),
      },
      {
        section: Section.AIGeneration,
        URLParts: { scope: "manage", path: "gpt/prompt-suggestions" },
        header: "Prompt Snippets",
        description:
          "An easy way to manage commonly used text snippets in a prompt instruction.",
        example: (
          <>
            A <i>Prompt Snippet</i> is a text which can be inserted into a
            prompt instruction. When updating a snippet, all prompts which use
            that snippet will be updated. The snippet is referenced with a
            placeholder in the instruction and will be inserted at runtime.
          </>
        ),
      },
      {
        section: Section.AIGeneration,
        URLParts: { scope: "manage", path: "gpt/prompt-selection-rules" },
        header: "Prompt Selection Rules",
        description: "Automatically select a prompt based on data.",
        example: (
          <>
            A <i>Prompt Selection Rule</i> looks up data in a <i>Document</i>{" "}
            and selects a prompt based on that data. If a rule is matched when
            opening a <i>Document</i>, the corresponding prompt is pre-selected.
            This can also be used to dynamically select different prompts for
            multiple <i>Documents</i> when generating AI content in bulk.
          </>
        ),
      },
    ],
  },
  {
    section: Section.Translation,
    title: "Translation",
    icon: "translate",
    summaries: [
      {
        section: Section.Translation,
        URLParts: {
          scope: "manage",
          path: "machine-translation/machine-translation-config-group/",
        },
        header: "Machine Translation Settings",
        description: "Manage settings for machine translation.",
        example: (
          <>
            Set up per language which provider and other settings should be used
            for machine translation.
            <br />
            You can also create multiple sets of settings for different
            purposes.
          </>
        ),
      },
      {
        section: Section.Translation,
        URLParts: { scope: "manage", path: "gpt/prompts/translation" },
        header: "Translation Prompts",
        description:
          "Further customize AI translation with customized prompts.",
        example: (
          <>
            A <i>Translation Prompt</i> is a prompt which is used to translate
            text with AI.
            <br />
            When translating a text with AI, a standard prompt is used by
            default. With a <i>Translation Prompt</i> you can further customize
            the translation for specific purposes by giving instructions on how
            to translate.
          </>
        ),
      },
      {
        section: Section.Translation,
        URLParts: { scope: "manage", path: "languages/" },
        header: "Languages",
        description: "View available languages and their settings.",
        example: (
          <>
            Configure master language and specify mail address for translation
            &amp; proofreading requests.
          </>
        ),
      },
    ],
  },
  {
    section: Section.TextControl,
    title: "Text Control",
    icon: "transform",
    summaries: [
      {
        section: Section.TextControl,
        URLParts: { scope: "manage", path: "glossaries/" },
        header: "Glossaries",
        description:
          "Specify term translations to be used in machine translation.",
        example: (
          <>
            A <i>Glossary</i> specifies how a word or term should be translated
            in a specific language pair.
            <br />
            Glossaries are <b>not</b> supported when translating with GPT.
          </>
        ),
      },
      {
        section: Section.TextControl,
        URLParts: { scope: "manage", path: "translation-memory" },
        header: "Translation Memory",
        description:
          "Store and reuse previous translations to maintain consistency.",
        example: (
          <>
            A <i>Translation Memory</i> stores source text and its translations
            for a language pair.
            <br />
            When translating similar text, the system can suggest existing
            translations to ensure consistency across your content.
          </>
        ),
      },
      {
        section: Section.TextControl,
        URLParts: { scope: "manage", path: "find-and-replace" },
        header: "Find and Replace",
        description:
          "Automatically apply find and replace rules in generated and translated texts.",
        example: (
          <>
            Use <i>Find and Replace</i> to automatically identify and replace
            specific words or phrases in generated and translated texts.
            <br />
            Regular expressions (Regex) is supported.
          </>
        ),
      },
      {
        section: Section.TextControl,
        URLParts: { scope: "manage", path: "no-translate-list" },
        header: "No-Translate Word List",
        description: "Specify words or terms which should never be translated.",
        example: (
          <>
            <i>No-Translate Word List</i> is a list of words or terms which
            should never be translated by machine translation.
            <br />
            Use to prevent certain words or phrases from being translated, for
            example to preserve names or specific terminology. Regular
            expressions (Regex) is supported.
            <br />
            <i>No-Translate Word List</i> is <b>not</b> supported when
            translating with GPT.
          </>
        ),
      },
      {
        section: Section.TextControl,
        URLParts: { scope: "manage", path: "vocabulary/template-labels" },
        header: "Template Settings",
        description: "Manage what settings are available for templates.",
        example: (
          <>
            Set up which <i>Template Settings</i> should be available for use in
            templates. Such as which section they should be inserted into, and
            if they should have any formatting.
          </>
        ),
      },
      {
        section: Section.TextControl,
        URLParts: { scope: "manage", path: "link-replacements" },
        header: "SEO Crosslinks",
        description:
          "Automatically inject hyperlinks into texts for increased SEO.",
        example: (
          <>
            Use <i>SEO Crosslinks</i> to automatically identify and replace
            specific words or phrases in texts with hyperlinks.
            <br />
            Specify which keywords should be linked and to which URL.
          </>
        ),
      },
      {
        section: Section.TextControl,
        URLParts: { scope: "manage", path: "style-guide/" },
        header: "Style Guide",
        description: "Describe what style translations should be in.",
        example: (
          <>
            Will be used by translators when doing Proofreading/Manual
            translation.
          </>
        ),
      },
    ],
  },
  {
    section: Section.Logs,
    title: "Logs",
    icon: "format_align_left",
    summaries: [
      {
        section: Section.Logs,
        URLParts: { scope: "manage", path: "publish-logs/" },
        header: "Publish Logs",
        description: "View logs for published documents.",
      },
      {
        section: Section.Logs,
        URLParts: { scope: "manage", path: "gpt/generation-events/" },
        header: "AI Generation Logs",
        description: "View logs for AI generation.",
      },
      {
        section: Section.Logs,
        URLParts: {
          scope: "manage",
          path: "machine-translation/machine-translation-events/",
        },
        header: "Machine Translation Logs",
        description: "View logs for machine translation.",
      },
    ],
  },
  {
    section: Section.Other,
    title: "Other",
    icon: "tune",
    summaries: [
      {
        section: Section.Other,
        URLParts: { scope: "manage", path: "filter-groups/" },
        header: "Filters",
        description: "Manage saved filters and filter groups.",
        example: (
          <>
            Save commonly used <i>Filter</i> in the <i>Document List</i> to
            quickly re-apply it in the future.
            <br />A <i>Filter Group</i> is a semantic grouping of filters.
          </>
        ),
      },
      {
        section: Section.Other,
        URLParts: { scope: "manage", path: "channel-language" },
        header: "Channels",
        description: "Manage channels.",
        example: (
          <>
            A <i>Channel</i> is connected to one <i>Document Structure</i> and
            has settings for how a <i>Document</i> should be published.
          </>
        ),
      },
      {
        section: Section.Other,
        URLParts: { scope: "manage", path: "template-categories/" },
        header: "Template Categories",
        description: "Manage template categories.",
        example: (
          <>
            Group multiple templates into a category to apply them quicker to a{" "}
            <i>Document</i>.
            <br />
            When a Template Category is saved, it also saves the current values
            of all variables in the templates.
          </>
        ),
      },
      {
        section: Section.Other,
        URLParts: { scope: "manage", path: "options" },
        header: "Feature options",
        description: "Configure enabled features.",
        example: (
          <>
            Enable or disable different features throughout the platform.
            <br />
            Note, some features are legacy and not supported anymore.
          </>
        ),
      },
      {
        section: Section.Other,
        URLParts: { scope: "manage", path: "api-settings" },
        header: "Publish Settings",
        description: "Manage settings for publishing Documents.",
        example: (
          <>
            Specify external endpoints to publish <i>Documents</i> to.
            <br />
            View the API Documentation.
          </>
        ),
      },
      {
        section: Section.Other,
        URLParts: { scope: "manage", path: "note/" },
        header: "Notes",
        description: "View or edit notes.",
        example: (
          <>
            These can be filled in by you or Textual to be used as reference for
            the team.
          </>
        ),
      },
    ],
  },
  {
    section: Section.Account,
    title: "Account",
    icon: "business",
    summaries: [
      {
        section: Section.Account,
        URLParts: { scope: "customer", path: "account/" },
        header: "Account",
        description: "View and manage your organization.",
        example: (
          <>
            This includes user management, billing information and other
            settings.
          </>
        ),
        visibleWhen: "showBilling",
      },
      {
        section: Section.Account,
        URLParts: { scope: "customer", path: "auth/profile" },
        header: "My Profile",
        description: "View and manage your profile.",
        example: (
          <>This includes your username, email address and other settings.</>
        ),
        visibleWhen: "isAuthenticated",
      },
    ],
  },
];

type SettingsLandingPageProps = {
  customerSlug?: string;
  isAuthenticated?: string;
  showBilling?: string;
};

const resolveURL = (
  input: URLParts,
  buildURL: (path: string) => string,
  buildManageURL: (path: string) => string
): string => {
  return input.scope === "manage"
    ? buildManageURL(input.path)
    : buildURL(input.path);
};

const isSummaryVisible = (
  summary: SettingsSummary,
  showBilling?: string,
  isAuthenticated?: string
): boolean => {
  if (summary.visibleWhen === "showBilling") {
    return showBilling === "true";
  }
  if (summary.visibleWhen === "isAuthenticated") {
    return isAuthenticated === "true";
  }
  return true;
};

export const SettingsLandingPage: React.FC<SettingsLandingPageProps> = ({
  customerSlug = "",
  isAuthenticated,
  showBilling,
}) => {
  const [searchValue, setSearchValue] = useState<string>(
    new URL(window.location.href).searchParams.get("search") ?? null
  );

  useEffect(() => {
    const url = new URL(window.location.href);

    if (searchValue === null || searchValue.trim() == "") {
      url.searchParams.delete("search");
      window.history.replaceState({}, "", url.toString());
      return;
    }

    url.searchParams.set("search", searchValue);

    window.history.replaceState({}, "", url.toString());
  }, [searchValue]);

  const filterSections = (query: string): SettingsSectionGroup[] => {
    const q = query.toLowerCase();
    return SETTINGS_SECTIONS.map((g) => {
      if (g.section.includes(q)) {
        return g;
      }
      const filteredSummaries = g.summaries.filter(
        (s) =>
          s.description.toLowerCase().includes(q) ||
          s.header.toLowerCase().includes(q)
      );
      return filteredSummaries.length > 0
        ? { ...g, summaries: filteredSummaries }
        : null;
    }).filter((g) => g != null);
  };

  const filteredSections =
    searchValue != null ? filterSections(searchValue) : SETTINGS_SECTIONS;

  const buildURL = (path: string): string => {
    const tail = path.replace(/^\//, "");
    return `/d/${customerSlug}/${tail}`;
  };

  const buildManageURL = (path: string): string => {
    return buildURL(`manage/${path}`);
  };

  const mainSections = filteredSections.filter(
    (g) => g.section !== Section.Account
  );
  const accountSection = filteredSections.find(
    (g) => g.section === Section.Account
  );

  const accountSummaries =
    accountSection?.summaries.filter((s) =>
      isSummaryVisible(s, showBilling, isAuthenticated)
    ) ?? [];

  const showAccountGroup =
    (showBilling === "true" || isAuthenticated === "true") &&
    accountSummaries.length > 0;

  return (
    <div className="txu-manage-container" style={{ scrollbarGutter: "stable" }}>
      <h1>Settings</h1>
      <SearchBar
        className="tw-mb-6"
        placeholder="Search for a settings group..."
        value={searchValue ?? ""}
        onValueChange={setSearchValue}
      />
      {mainSections.map((group) => (
        <div key={group.section} className="txu-manage-group">
          <h2 className="tw-flex tw-items-center">
            <i className="material-icons tw-mx-2">{group.icon}</i>
            {group.title}
          </h2>
          <section>
            {group.summaries.map((summary) => (
              <SettingsCard
                key={`${summary.section}-${summary.header}`}
                href={resolveURL(summary.URLParts, buildURL, buildManageURL)}
                header={summary.header}
                description={summary.description}
                example={summary.example}
              />
            ))}
          </section>
        </div>
      ))}
      {showAccountGroup && accountSection != null && (
        <div className="txu-manage-group">
          <h2 className="tw-flex tw-items-center">
            <i className="material-icons tw-mx-2">{accountSection.icon}</i>
            {accountSection.title}
          </h2>
          <section>
            {accountSummaries.map((summary) => (
              <SettingsCard
                key={`${summary.section}-${summary.header}`}
                href={resolveURL(summary.URLParts, buildURL, buildManageURL)}
                header={summary.header}
                description={summary.description}
                example={summary.example}
              />
            ))}
          </section>
        </div>
      )}
    </div>
  );
};
